import { useEffect, useRef, type ComponentType, type ReactNode } from 'react';
import { plannerDetailsColumnConfig } from '@/app/KeywordResearch/config/plannerDetailsColumnConfig';
import { Col, Row } from 'react-bootstrap';
import { useLocation, useOutletContext } from 'react-router-dom';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import {
  useGetHistorySearchVolumeQuery,
  useGetSearchVolumeFuncMutation,
  useClusterSearchVolumeFromTaskMutation
} from '@/app/KeywordResearch/api/searchVolumeApi';
import { useExportTableMutation } from '@/app/KeywordResearch/api/keywordPlannerApi';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import { FaDesktop, FaMobileScreenButton } from 'react-icons/fa6';
import SearchVolumeForm from '../components/keyword-sv/SearchVolumeForm';
import SearchVolumeProgressBar from '../components/keyword-sv/SearchVolumeProgressBar';
import RemovedKeywordsAlert from '../components/keyword-sv/RemovedKeywordsAlert';
import StatisticsCard from '../components/StatisticsCard';
import bg1 from '@/assets/img/icons/spot-illustrations/corner-1.png';
import bg2 from '@/assets/img/icons/spot-illustrations/corner-2.png';
import bg3 from '@/assets/img/icons/spot-illustrations/corner-3.png';
import bg4 from '@/assets/img/icons/spot-illustrations/corner-4.png';
import bg5 from '@/assets/img/icons/spot-illustrations/corner-5.png';
import bg6 from '@/assets/img/icons/spot-illustrations/corner-6.png';
import FiltersComponent from '@/app/common/components/filters/FiltersComponent';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import DataLoadError from '@/components/errors/DataLoadError';
import { getActiveQueryError } from '@/app/common/utils/apiErrorUtils';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import FiltersComponentSkeleton from '../../common/components/filters/FiltersComponentSkeleton';
import IconAlert from '@/components/common/IconAlert';
import useDomain from '@/hooks/useDomain';
import { intentMap, purposeMap } from '../config/keywordGapColumnConfig';
import RemovedKeywordsModal from '../components/RemovedKeywordsModal';
import ClusterFromTaskButton from '@/app/KeywordResearch/components/ClusterFromTaskButton';

type BreadcrumbItem = { text: string; href: string };

type OutletContext = [BreadcrumbItem[], (items: BreadcrumbItem[]) => void];

type StatItem = {
  title: ReactNode;
  value: number | undefined;
  image: string;
  className?: string;
};

type AgGridWrapperProps = {
  table_name?: string;
  height: string;
  columns: unknown[];
  data?: unknown[];
  total?: number;
  fetchFunction?: unknown;
  additionalData?: Record<string, unknown>;
  settings?: Record<string, unknown>;
};

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

const SearchVolume = () => {
  const gridRef = useRef<HTMLDivElement>(null);
  const { domain_slug } = useDomain();
  const access = useToolAccess('search_volume');
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const { data: userInfo } = useGetUserInfoQuery();
  const [exportTable] = useExportTableMutation();
  const [getSearchVolumeFunc] = useGetSearchVolumeFuncMutation();
  const [clusterSearchVolumeFromTask, { isLoading: isClustering }] =
    useClusterSearchVolumeFromTaskMutation();
  const { pathname } = useLocation();

  useEffect(() => {
    setBreadcrumbData([{ text: 'میزان جستجو', href: '#' }]);
  }, [setBreadcrumbData]);

  const { data, isError, isFetching, isLoading, error, refetch } =
    useGetHistorySearchVolumeQuery(undefined, {
      skip: access.isLoading || !access.haveAccess
    });

  if (access.isLoading) return <></>;

  if (!access.haveAccess) {
    return (
      <ToolAccessDenied access={access} tool_label="ابزار میزان جستجو" />
    );
  }

  const queryError = getActiveQueryError([
    { isError, error, refetch, isFetching },
    {
      isError: access.isError,
      error: access.error,
      refetch: access.refetch,
      isFetching: access.isFetching,
    },
  ]);

  if (queryError) return <DataLoadError {...queryError} />;

  const isProcessing = data?.data?.status === 'processing';
  const taskId = data?.data?.task_id;

  const statItems: StatItem[] = [
    {
      title: 'تعداد کلمات',
      value: data?.data?.num_of_keywords,
      image: bg1
    },
    {
      title: 'میزان جستجو',
      value: data?.data?.sum_of_search_volumes,
      image: bg2
    },
    {
      title: (
        <div>
          تخمین کلیک رتبه یک <FaDesktop />
        </div>
      ),
      value: data?.data?.ctr_rank_one_desktop,
      image: bg3
    },
    {
      title: (
        <div>
          تخمین کلیک رتبه یک <FaMobileScreenButton />
        </div>
      ),
      value: data?.data?.ctr_rank_one_mobile,
      image: bg4
    },
    {
      title: <div>تخمین حداقل هزینه (تومان)</div>,
      value: data?.data?.min_cost_per_click,
      image: bg5
    },
    {
      title: <div>تخمین حداکثر هزینه (تومان)</div>,
      value: data?.data?.max_cost_per_click,
      image: bg6
    }
  ];

  return (
    <>
      
      <Row>
        <Col lg={6} xs={12} className="mb-3">
          <SearchVolumeForm />
        </Col>
        <Col lg={6} xs={12} className="mb-3">
          <Row className="g-3 mb-3">
            {!isLoading && !isFetching && !access.isLoading && !isProcessing
              ? statItems.map(({ title, value, className, image }, index) => (
                  <Col key={index} xs={6} lg={6}>
                    <StatisticsCard
                      stat={{
                        title,
                        value,
                        decimal: false,
                        compact: true,
                        image,
                        className:'py-1',
                      }}
                    />
                  </Col>
                ))
              : Array.from({ length: 6 }).map((_, index) => (
                  <Col key={index} xs={6} lg={6}>
                    <TextLoading height={98} />
                  </Col>
                ))}
          </Row>
        </Col>
      </Row>
      {isProcessing && taskId && (
        <Row>
          <Col xxl={12}>
            <SearchVolumeProgressBar taskId={taskId} active={isProcessing} />
          </Col>
        </Row>
      )}
      <Row>
        <RemovedKeywordsModal volumeData={data?.data} />
        {!isLoading && !isFetching && !access.isLoading ? (
          <>
            <Col xxl={12}>
              {!data?.data?.rows?.length && (
                <IconAlert variant="warning">
                  برای کلمه شما داده‌ای یافت نشد، بهتره کلمه دیگه رو تست کنید.
                  در نظر داشته باشید گوگل ادز برای برخی کلمات پزشکی محدودیت داره{' '}
                  <a
                    target="_blank"
                    rel="noreferrer noopener"
                    href="https://help.mizfa.tools/reason-for-not-displaying-keyword-search-volume/"
                  >
                    (اطلاعات بیشتر)
                  </a>
                  .{' '}
                </IconAlert>
              )}
              <RemovedKeywordsAlert volumeData={data?.data} />
            </Col>
            <Col xxl={12}>
              <FiltersComponent
                selectItems={{
                  purpose: Object.entries(purposeMap).map(([k, v]) => ({
                    label: v,
                    value: k
                  })),
                  intent: Object.entries(intentMap).map(([k]) => ({
                    label: k,
                    value: k
                  }))
                }}
                columns={plannerDetailsColumnConfig(pathname, {
                  location_code: data?.data?.location_code,
                  language_code: data?.data?.language_code
                })}
              />
            </Col>
            <Col xxl={12}>
              <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                <ClusterFromTaskButton
                  taskId={taskId}
                  keywordCount={
                    data?.data?.num_of_keywords ?? data?.data?.total ?? 0
                  }
                  mutate={clusterSearchVolumeFromTask}
                  isClustering={isClustering}
                  isTaskReady={!isProcessing}
                />
              </div>
              <div ref={gridRef}>
                {data?.data ? (
                  <TypedAgGridWrapper
                    table_name={pathname.split('/').slice(2, 3).join('/')}
                    settings={{
                      pagination: true,
                      fullscreen: false,
                      changeHeight: false,
                      resetGrid: false,
                      saveGrid: false,
                      export: {
                        enabled: true,
                        fetchFunction: exportTable,
                        additionalData: { task_id: data.data.task_id }
                      }
                    }}
                    height="746px"
                    columns={plannerDetailsColumnConfig(pathname, {
                      location_code: data.data.location_code,
                      language_code: data.data.language_code
                    })}
                    total={data.data.total}
                    additionalData={{
                      task_id: data.data.task_id,
                      requested_user: {
                        name_family: `${userInfo?.data?.first_name} ${userInfo?.data?.last_name}`,
                        email: userInfo?.data?.email
                      }
                    }}
                    fetchFunction={getSearchVolumeFunc}
                    data={data.data.rows}
                  />
                ) : (
                  <TypedAgGridWrapper
                    settings={{
                      pagination: true,
                      fullscreen: false,
                      changeHeight: false,
                      resetGrid: false,
                      saveGrid: false
                    }}
                    height="696px"
                    columns={plannerDetailsColumnConfig(pathname, {
                      location_code: data?.data?.location_code,
                      language_code: data?.data?.language_code
                    })}
                    data={[]}
                  />
                )}
              </div>
            </Col>
          </>
        ) : (
          <>
            <Col xxl={12} className="mb-2">
              <FiltersComponentSkeleton />
            </Col>
            <Col xxl={12}>
              <AgGridSkeleton
                height="550px"
                settings={{
                  pagination: false,
                  fullscreen: false,
                  changeHeight: false,
                  resetGrid: false,
                  saveGrid: false,
                  exportBtn: false
                }}
              />
            </Col>
          </>
        )}
      </Row>
    </>
  );
};

export default SearchVolume;
